import React, { useState, useEffect } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import Loader from "react-js-loader";
import Navbar from "../navbar/Navbar";

const API_KEY = process.env.REACT_APP_API_KEY;
const genAI = new GoogleGenerativeAI(API_KEY);

const questions = [
  "How often have you felt down, depressed, or hopeless in the past two weeks?",
  "How often do you feel little interest or pleasure in doing things?",
  "How often do you feel nervous, anxious, or on edge?",
  "How often do you have trouble relaxing?",
  "How often do you feel so restless that it is hard to sit still?",
  "How often do you feel fatigued or have little energy?",
  "How often do you feel bad about yourself, or that you are a failure or have let yourself or your family down?",
  "How often do you have trouble concentrating on things, such as reading or watching TV?",
  "How often do you feel afraid, as if something awful might happen?",
  "How often do you have trouble falling or staying asleep, or sleeping too much?",
  "How often do you feel easily annoyed or irritable?",
  "How often do you experience physical symptoms such as headaches, stomachaches, or muscle pain?",
  "How often do you feel disconnected or detached from reality or your surroundings?",
  "How often do you find it difficult to control your worry?",
  "How often do you avoid social situations due to fear of being judged or embarrassed?",
];

const options = [
  "Not at all",
  "Several days",
  "More than half the days",
  "Nearly every day",
];

const Quiz = () => {
  const [answers, setAnswers] = useState(Array(questions.length).fill(""));
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ Hide page scroll when quiz is active
  useEffect(() => {
    document.body.style.overflowY = "hidden";
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  const handleChange = (value) => {
    const updated = [...answers];
    updated[currentQuestion] = value;
    setAnswers(updated);
  };

  const handleNext = () => {
    if (!answers[currentQuestion]) {
      alert("Please select an option before continuing.");
      return;
    }
    setCurrentQuestion((prev) => Math.min(prev + 1, questions.length - 1));
  };

  const handlePrevious = () => {
    setCurrentQuestion((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (answers.some((a) => a === "")) {
      alert("Please answer all questions before submitting.");
      return;
    }

    setLoading(true);
    setResult("");

    try {
      const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

      const prompt = `
You are a mental health assistant. 
Analyze the following quiz responses and generate:
- A short summary of the person's emotional state.
- Key points on mental health observations.
- Personalized suggestions for improvement.
Tone: calm, warm, and professional.
Generate formatted text suitable for a webpage.

Answers:
${questions.map((q, i) => `${i + 1}. ${q} — ${answers[i]}`).join("\n")}
`;

      const res = await model.generateContent(prompt);
      const text = (await res.response.text())
        .replace(/\*\*(.*?)\*\*/g, "$1")
        .replace(/##/g, "\n\n")
        .trim();

      setResult(text);
    } catch (err) {
      console.error(err);
      setResult("❌ Error: Could not analyze the answers. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* ✅ overflow-y hidden ensures no vertical scroll */}
      <div className="h-screen overflow-y-hidden flex flex-col items-center justify-center bg-gradient-to-br from-[#FFE9E3] via-[#FFF3EE] to-[#FFEFEA] px-4">
        <div className="max-w-5xl w-full bg-white/70 backdrop-blur-md p-8 rounded-3xl shadow-xl border border-[#FFD5C2]">
          <h1 className="text-2xl font-extrabold text-center mb-8 text-[#2B2B2B]">
            🌸 Mindfulness Self-Assessment
          </h1>

          {!result && (
            <>
              <div className="flex justify-center mb-6">
                <div className="w-full max-w-xl bg-white p-5 rounded-2xl shadow-md border border-[#FFD5C2]">
                  <p className="mb-4 text-md font-semibold text-gray-800 text-center leading-snug">
                    {currentQuestion + 1}. {questions[currentQuestion]}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {options.map((option) => (
                      <label
                        key={option}
                        className={`cursor-pointer text-center border-2 rounded-full px-4 py-2 text-sm font-medium transition duration-200 ${
                          answers[currentQuestion] === option
                            ? "bg-[#FF9F87] text-white border-[#FF9F87]"
                            : "bg-white hover:bg-[#FFF0EC] text-gray-700 border-[#FFD5C2]"
                        }`}
                      >
                        <input
                          type="radio"
                          name={`q-${currentQuestion}`}
                          value={option}
                          checked={answers[currentQuestion] === option}
                          onChange={() => handleChange(option)}
                          className="hidden"
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between mt-2">
                <button
                  onClick={handlePrevious}
                  disabled={currentQuestion === 0}
                  className="bg-[#F5D0C1] hover:bg-[#FFBFA9] text-gray-800 px-5 py-2 rounded-full text-sm transition disabled:opacity-50"
                >
                  Previous
                </button>
                {currentQuestion < questions.length - 1 ? (
                  <button
                    onClick={handleNext}
                    className="bg-[#FF9F87] hover:bg-[#FF8E75] text-white px-5 py-2 rounded-full text-sm transition"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className={`bg-[#7CC6B8] text-white px-5 py-2 rounded-full text-sm transition ${
                      loading
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:bg-[#68B5A4]"
                    }`}
                  >
                    {loading ? "Analyzing..." : "Submit"}
                  </button>
                )}
              </div>
            </>
          )}

          {loading && (
            <div className="flex justify-center mt-4">
              <Loader
                type="spinner-cub"
                bgColor="#FF9F87"
                color="#FF9F87"
                size={80}
              />
            </div>
          )}

          {!loading && result && (
            <div className="mt-8 p-6 bg-white/80 rounded-2xl border border-[#FFD5C2] text-gray-800 shadow-md overflow-y-auto max-h-[70vh]">
              <h2 className="text-xl font-semibold mb-3 text-[#2B2B2B]">
                💬 AI Reflection
              </h2>
              <p className="whitespace-pre-wrap leading-relaxed text-sm">{result}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Quiz;
